import { useCalcNavbarHeight } from '@/hooks/useCalcNavbarHeight';
import usePersistFn from '@/hooks/usePersistFn';
import { useClientI18nModelStore } from '@/models/client-i18n/clientI18n';
import { Anchor } from 'aui';
import { FoxIconMenu02 } from 'aui-biz-icons';
import clsx from 'clsx';
import React from 'react';
import styles from './DocAnchor.module.css';
import { useDocAnchorHeadingList } from './hooks/useDocAnchorHeadingList';
import { useDocAnchorPosition } from './hooks/useDocAnchorPosition';

export type DocAnchorProps = {
  docContentRef: React.RefObject<HTMLDivElement>;
  className?: string;
};

export function DocAnchor(props: DocAnchorProps) {
  const { docContentRef, className } = props;

  const navbarHeight = useCalcNavbarHeight();
  const anchorPosition = useDocAnchorPosition(navbarHeight);
  const { headingList, minDepth } = useDocAnchorHeadingList(docContentRef);
  const anchorListRef = React.useRef<HTMLDivElement>(null);
  const isAnchorHoveringRef = React.useRef(false);
  const clientI18n = useClientI18nModelStore((x) => x.clientI18n);

  const handleAnchorChange = usePersistFn((currentActiveLink: string) => {
    if (!anchorListRef.current) {
      return;
    }

    const activeElement = anchorListRef.current.querySelector<HTMLDivElement>(
      `a[href = '${currentActiveLink}']`,
    );
    if (!activeElement) {
      return;
    }

    if (!isAnchorHoveringRef.current) {
      anchorListRef.current.scrollTo(
        0,
        activeElement.offsetTop - anchorListRef.current.offsetHeight / 2,
      );
    }
  });

  return (
    <div
      className={clsx(
        'sticky hidden h-[100px] w-[300px] p-5 pt-0 @[960px]/doc-content:block',
        {
          ['pointer-events-none']: !headingList.length,
        },
        styles.anchor,
        className,
      )}
      style={anchorPosition}
    >
      {!!headingList.length && (
        <div
          ref={anchorListRef}
          className="h-full overflow-y-auto"
          onMouseEnter={() => {
            isAnchorHoveringRef.current = true;
          }}
          onMouseLeave={() => {
            isAnchorHoveringRef.current = false;
          }}
        >
          <div className="mb-3 text-semi-lg font-500 text-color-sub">
            <FoxIconMenu02 size={14} className="mr-2" />
            {clientI18n('doc.anchor.title')}
          </div>

          <Anchor
            targetOffset={navbarHeight}
            affix={false}
            showInkInFixed={true}
            className="!max-h-none"
            getContainer={() => {
              return document.getElementById('main-scroll-container') || document.body;
            }}
            onChange={handleAnchorChange}
          >
            {headingList.map((res) => (
              <div key={res.id} style={{ paddingLeft: `${(res.depth - minDepth) * 16}px` }}>
                <Anchor.Link
                  className={clsx(
                    'truncate px-0 py-1 text-semi-lg text-secondary hover:text-primary',
                    {
                      ['font-base']: res.depth === minDepth,
                      ['font-400']: res.depth !== minDepth,
                    },
                  )}
                  key={res.text}
                  href={`#${res.id}`}
                  title={<span dangerouslySetInnerHTML={{ __html: res.text }} />}
                />
              </div>
            ))}
          </Anchor>
        </div>
      )}
    </div>
  );
}
